export function formatTotemPrice(amount: number): string {
  // Truncate to 2 decimals to prevent rounding up, and force 2 decimal display
  const truncatedAmount = Math.trunc(amount * 100) / 100;
  const hasDecimals = truncatedAmount % 1 !== 0;
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(truncatedAmount);
}

/**
 * Trunca a 2 decimales sin redondear hacia arriba (criterio de caja del tótem).
 * El `+ 1e-7` absorbe el residuo binario del punto flotante (2430.029999… ⇒ 2430.03) sin
 * tocar valores que genuinamente estén por debajo del centavo.
 */
export const trunc2 = (value: number) => Math.trunc(value * 100 + 1e-7) / 100;

export function calculateCartItemTotal(item: {
  unitPrice: number;
  quantity: number;
  subselections: { price: number; quantity: number }[];
}): number {
  const subselectionTotal = item.subselections.reduce((sum, sub) => sum + sub.price * sub.quantity, 0);
  return (item.unitPrice + subselectionTotal) * item.quantity;
}

/**
 * Precio promocionado de una unidad (promo por producto/categoría). **Regla única de centavos:**
 * el descuento se trunca a 2 decimales ANTES de restar; el precio final queda truncado.
 * Es la misma regla que usa el servidor al persistir la orden, así carrito y orden coinciden.
 */
export function calculatePromoUnitPrice(
  price: number,
  promo: { discount: number; discountType: 'percentage' | 'fixed' } | null | undefined
): { discountAmount: number; finalPrice: number } {
  const safePrice = trunc2(Math.max(0, Number.isFinite(price) ? price : 0));

  if (!promo || !Number.isFinite(promo.discount) || promo.discount <= 0) {
    return { discountAmount: 0, finalPrice: safePrice };
  }

  const rawDiscount = promo.discountType === 'percentage' ? (safePrice * promo.discount) / 100 : promo.discount;
  const discountAmount = Math.min(safePrice, trunc2(Math.max(0, rawDiscount)));

  return { discountAmount, finalPrice: trunc2(safePrice - discountAmount) };
}

/** Línea del carrito para el cálculo de totales (bruto usa `originalPrice` cuando la promo lo guardó). */
export type CartLineInput = {
  unitPrice: number;
  originalPrice?: number;
  quantity: number;
  subselections: { price: number; quantity: number }[];
};

export type CartTotals = {
  /** Σ (precio de lista + subselecciones) × cantidad (antes de promos). */
  subtotalBruto: number;
  /** Σ (precio con promos + subselecciones) × cantidad. */
  subtotal: number;
  /** `subtotalBruto − subtotal`: lo que ahorran las promos por línea. */
  promoDiscountAmount: number;
  /** Descuento del cupón de carrito, truncado antes de restar. */
  couponDiscountAmount: number;
  /** Neto final del carrito (stacking: cupón sobre el subtotal ya promocionado). */
  total: number;
};

/**
 * Totales del carrito con la regla de dinero canónica:
 * - promos ya vienen en `unitPrice` (truncadas por unidad en `calculatePromoUnitPrice`);
 * - cupón (porcentaje o fijo) se trunca a 2 decimales antes de restar, y se aplica **sobre el
 *   subtotal promocionado** (decisión: cupón sobre el monto ya descontado).
 * El servidor usa las mismas funciones al persistir, así los centavos coinciden.
 */
export function calculateCartTotals(
  items: CartLineInput[],
  discount: { amount: number; type: 'percentage' | 'fixed' } | null | undefined
): CartTotals {
  let subtotal = 0;
  let subtotalBruto = 0;

  for (const item of items) {
    const quantity = Math.max(0, Number.isFinite(item.quantity) ? item.quantity : 0);
    const subselectionTotal = item.subselections.reduce((sum, sub) => sum + sub.price * sub.quantity, 0);
    const unitPrice = Number.isFinite(item.unitPrice) ? item.unitPrice : 0;

    subtotal += (unitPrice + subselectionTotal) * quantity;
    const fullUnitPrice =
      item.originalPrice != null && Number.isFinite(item.originalPrice) ? item.originalPrice : unitPrice;
    subtotalBruto += (fullUnitPrice + subselectionTotal) * quantity;
  }

  const promoDiscountAmount = Math.max(0, subtotalBruto - subtotal);

  const couponDiscountAmount = discount
    ? discount.type === 'percentage'
      ? Math.min(subtotal, trunc2((subtotal * discount.amount) / 100))
      : Math.min(subtotal, trunc2(discount.amount))
    : 0;

  const total = trunc2(Math.max(0, subtotal - couponDiscountAmount));

  // Truncado final: las multiplicaciones de punto flotante dejan residuo.
  return {
    subtotalBruto: trunc2(subtotalBruto),
    subtotal: trunc2(subtotal),
    promoDiscountAmount: trunc2(promoDiscountAmount),
    couponDiscountAmount,
    total,
  };
}

/**
 * Total neto de una orden del tótem: mismo redondeo que el carrito (`Math.trunc`, no redondea
 * hacia arriba) para que lo que se persiste coincida con el total que vio el cliente.
 * Un descuento mayor al bruto nunca deja un total negativo.
 */
export function calculateDiscountedOrderTotal(
  grossSubtotal: number,
  discount: { amount: number; type: 'percentage' | 'fixed' } | null | undefined
): { discountAmount: number; netTotal: number } {
  const safeGross = trunc2(Math.max(0, Number.isFinite(grossSubtotal) ? grossSubtotal : 0));

  if (!discount || discount.amount <= 0) {
    return { discountAmount: 0, netTotal: safeGross };
  }

  const rawDiscount = discount.type === 'percentage' ? (safeGross * discount.amount) / 100 : discount.amount;
  const discountAmount = Math.min(safeGross, trunc2(Math.max(0, rawDiscount)));

  return { discountAmount, netTotal: trunc2(Math.max(0, safeGross - discountAmount)) };
}
